import type { DocumentSlot, OnboardingStatus } from "@xop-any-time/shared";
import { Box, Typography } from "@mui/material";
import type { JSX } from "react";

import { signedDocumentUrl, type SignedDocumentKind } from "../../lib/onboardingApi.js";
import { WallCard } from "./OnboardingLayout.js";

/** Step 2 (spec E §10.2): nothing to do but wait — and see what was sent. */
export function UnderReviewStep({ status }: { status: OnboardingStatus }): JSX.Element {
  return (
    <WallCard title="Under review">
      <Typography component="p" sx={{ mt: 0.75, fontSize: 13.5, lineHeight: 1.6, color: "text.secondary" }}>
        We've sent your documents to XOP Networks. You'll get an email when your account is activated;
        this page will also update on its own.
      </Typography>

      <Box component="ul" aria-label="Uploaded documents" sx={{ mt: 3, p: 0, m: 0, listStyle: "none", display: "grid", gap: 1.5 }}>
        <UploadedRow label="Reseller Agreement" kind="signed_agreement" slot={status.documents.signedAgreement} />
        <UploadedRow label="NDA" kind="signed_nda" slot={status.documents.signedNda} />
      </Box>
    </WallCard>
  );
}

function UploadedRow({ label, kind, slot }: { label: string; kind: SignedDocumentKind; slot: DocumentSlot }): JSX.Element {
  const when = slot.uploadedAt
    ? new Date(slot.uploadedAt).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })
    : null;
  return (
    <Box component="li" sx={{ display: "flex", justifyContent: "space-between", gap: 2, borderRadius: 1.5, border: 1, borderColor: "divider", px: 2, py: 1.5 }}>
      <Box>
        <Typography component="p" sx={{ fontSize: 14, fontWeight: 600 }}>{label}</Typography>
        <Typography component="p" sx={{ fontSize: 12.5, color: "text.secondary" }}>
          {slot.attached ? `Uploaded${when ? ` ${when}` : ""}` : "Not uploaded"}
        </Typography>
      </Box>
      {slot.attached && slot.filename && (
        <Box
          component="a"
          href={signedDocumentUrl(kind)}
          download={slot.filename}
          sx={{ alignSelf: "center", fontSize: 13, fontWeight: 500, color: (t) => t.palette.accent[500], textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
        >
          {slot.filename}
        </Box>
      )}
    </Box>
  );
}
